/**
 * Performance Monitoring Utility
 *
 * Tracks Core Web Vitals and custom performance metrics.
 * Can be used to log metrics to analytics or monitoring services.
 */

export interface PerformanceMetrics {
  // Core Web Vitals
  FCP: number | null;  // First Contentful Paint
  LCP: number | null;  // Largest Contentful Paint
  FID: number | null;  // First Input Delay
  CLS: number | null;  // Cumulative Layout Shift
  TTFB: number | null; // Time to First Byte
  INP: number | null;  // Interaction to Next Paint

  // Additional metrics
  TTI: number | null;  // Time to Interactive (estimated)
  TBT: number | null;  // Total Blocking Time (estimated)

  // Page info
  url: string;
  timestamp: number;
}

type MetricCallback = (metric: { name: string; value: number; id: string }) => void;

/**
 * Initialize performance monitoring
 * Call this in your app entry point (main.tsx)
 */
export function initPerformanceMonitoring(
  onMetric?: MetricCallback,
  sendToAnalytics: boolean = false
): void {
  if (typeof window === 'undefined') return;

  const metrics: Partial<PerformanceMetrics> = {
    url: window.location.href,
    timestamp: Date.now(),
    FCP: null,
    LCP: null,
    FID: null,
    CLS: null,
    TTFB: null,
    INP: null,
    TTI: null,
    TBT: null,
  };

  // Helper to report metrics
  const reportMetric = (name: string, value: number, id: string = crypto.randomUUID()) => {
    (metrics as Record<string, number | null>)[name] = value;

    if (onMetric) {
      onMetric({ name, value, id });
    }

    if (sendToAnalytics) {
      sendToAnalyticsService(name, value);
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.log(`[Performance] ${name}: ${value.toFixed(2)}ms`);
    }
  };

  // First Contentful Paint (FCP)
  observePaint('first-contentful-paint', (entry) => {
    reportMetric('FCP', entry.startTime);
  });

  // Largest Contentful Paint (LCP)
  if ('PerformanceObserver' in window) {
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          reportMetric('LCP', lastEntry.startTime);
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {
      console.warn('[Performance] LCP observation not supported');
    }
  }

  // First Input Delay (FID)
  if ('PerformanceObserver' in window) {
    try {
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length > 0) {
          const entry = entries[0] as PerformanceEventTiming;
          reportMetric('FID', entry.processingStart - entry.startTime);
        }
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
    } catch (e) {
      console.warn('[Performance] FID observation not supported');
    }
  }

  // Cumulative Layout Shift (CLS)
  if ('PerformanceObserver' in window) {
    try {
      let clsValue = 0;
      let clsEntries: PerformanceEntry[] = [];

      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          // Only count layout shifts without recent user input
          if (!(entry as LayoutShift).hadRecentInput) {
            const firstSessionEntry = clsEntries[0];
            const lastSessionEntry = clsEntries[clsEntries.length - 1];

            // If the entry occurred less than 1 second after the previous entry and
            // less than 5 seconds after the first entry in the session, include the
            // entry in the current session. Otherwise, start a new session.
            if (
              firstSessionEntry &&
              entry.startTime - (lastSessionEntry as PerformanceEntry).startTime < 1000 &&
              entry.startTime - firstSessionEntry.startTime < 5000
            ) {
              clsValue += (entry as LayoutShift).value;
              clsEntries.push(entry);
            } else {
              clsValue = (entry as LayoutShift).value;
              clsEntries = [entry];
            }
          }
        }
        reportMetric('CLS', clsValue);
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
    } catch (e) {
      console.warn('[Performance] CLS observation not supported');
    }
  }

  // Time to First Byte (TTFB)
  if ('PerformanceObserver' in window) {
    try {
      const navObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length > 0) {
          const navEntry = entries[0] as PerformanceNavigationTiming;
          reportMetric('TTFB', navEntry.responseStart - navEntry.requestStart);
        }
      });
      navObserver.observe({ type: 'navigation', buffered: true });
    } catch (e) {
      console.warn('[Performance] TTFB observation not supported');
    }
  }

  // Interaction to Next Paint (INP)
  if ('PerformanceObserver' in window) {
    try {
      let maxINP = 0;
      const inpObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const eventEntry = entry as PerformanceEventTiming;
          const duration = eventEntry.duration;
          if (duration > maxINP) {
            maxINP = duration;
            reportMetric('INP', duration);
          }
        }
      });
      inpObserver.observe({ type: 'event', buffered: true });
    } catch (e) {
      console.warn('[Performance] INP observation not supported');
    }
  }

  // Report final metrics on page unload
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (import.meta.env.DEV) {
        console.log('[Performance] Final metrics:', metrics);
      }
    }
  });
}

/**
 * Observe paint timing entries
 */
function observePaint(
  paintType: string,
  callback: (entry: PerformanceEntry) => void
): void {
  if ('PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === paintType) {
            callback(entry);
          }
        }
      });
      observer.observe({ type: 'paint', buffered: true });
    } catch (e) {
      console.warn(`[Performance] Paint observation not supported for ${paintType}`);
    }
  }
}

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Send metric to analytics service
 * Replace this with your actual analytics implementation
 */
function sendToAnalyticsService(name: string, value: number): void {
  // Example: Send to Google Analytics 4
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', name, {
      event_category: 'Web Vitals',
      event_label: window.location.pathname,
      value: Math.round(value),
      non_interaction: true,
    });
  }

  // Example: Send to custom endpoint
  // This is commented out - enable when you have an endpoint
  /*
  navigator.sendBeacon('/api/analytics/vitals', JSON.stringify({
    metric: name,
    value,
    url: window.location.href,
    timestamp: Date.now(),
  }));
  */
}

/**
 * Get performance score based on Core Web Vitals thresholds
 * Returns 'good' | 'needs-improvement' | 'poor'
 */
export function getPerformanceRating(
  metric: 'LCP' | 'FID' | 'CLS' | 'FCP' | 'TTFB' | 'INP',
  value: number
): 'good' | 'needs-improvement' | 'poor' {
  const thresholds = {
    LCP: { good: 2500, poor: 4000 },
    FID: { good: 100, poor: 300 },
    CLS: { good: 0.1, poor: 0.25 },
    FCP: { good: 1800, poor: 3000 },
    TTFB: { good: 800, poor: 1800 },
    INP: { good: 200, poor: 500 },
  };

  const threshold = thresholds[metric];
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * TypeScript interfaces for Performance API entries
 */
interface LayoutShift extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
}

interface PerformanceEventTiming extends PerformanceEntry {
  processingStart: number;
  duration: number;
}
